import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "../lib/api";
import { format, subDays, formatDistanceToNow, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import {
  AlertTriangle, ArrowRight, Calendar, ChevronDown, ChevronRight,
  ChevronUp, Clock, ExternalLink, TrendingUp, Zap, DollarSign,
  FlaskConical, Trash2, Plus, Loader2, CheckCircle2, XCircle,
  Clock3,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import DashboardLayout from "../components/DashboardLayout";
import { toast } from "sonner";
import { API } from "../App";

// ─── Benchmark data ───────────────────────────────────────────────────────────
const SECTORS = {
  moda:           { label: "Moda",                  tatc:[8,14],  a2c:[25,40], c2b:[45,60], cvr:[1.5,3.0] },
  electronica:    { label: "Electrónica",           tatc:[6,12],  a2c:[20,35], c2b:[40,55], cvr:[1.0,2.5] },
  salud:          { label: "Salud y Belleza",       tatc:[10,18], a2c:[30,45], c2b:[50,65], cvr:[2.0,4.0] },
  hogar:          { label: "Hogar y Deco",          tatc:[8,14],  a2c:[25,40], c2b:[45,60], cvr:[1.5,3.5] },
  deportes:       { label: "Deportes/Outdoor",      tatc:[9,15],  a2c:[28,42], c2b:[48,62], cvr:[1.8,3.2] },
  juguetes:       { label: "Juguetes y Bebé",       tatc:[10,16], a2c:[30,45], c2b:[50,65], cvr:[2.0,3.5] },
  automocion:     { label: "Automoción (genérico)", tatc:[7,13],  a2c:[24,38], c2b:[45,60], cvr:[1.2,2.2] },
  recambios_moto: { label: "Recambios moto",        tatc:[7,13],  a2c:[25,38], c2b:[45,60], cvr:[1.3,2.3] },
  recambios_bici: { label: "Recambios bici",        tatc:[7,12],  a2c:[24,36], c2b:[45,60], cvr:[1.2,2.2] },
  mascotas:       { label: "Mascotas",              tatc:[10,18], a2c:[28,42], c2b:[45,65], cvr:[2.0,3.8] },
  alimentacion:   { label: "Alimentación",          tatc:[14,22], a2c:[35,55], c2b:[55,75], cvr:[3.0,6.0] },
};
const BUF = { tatc:1.0, a2c:2.0, c2b:3.0, cvr:0.3 };

const STAGE_LABELS = { tatc:"Tráfico → ATC", a2c:"ATC → Checkout", c2b:"Checkout → Compra", cvr:"Conversión total" };
const STAGE_CTA    = {
  tatc: { label:"Ver rendimiento de productos", to:"/analytics/products" },
  a2c:  { label:"Ver análisis GA4", to:"/analytics" },
  c2b:  { label:"Ver benchmark de conversión", scroll:"benchmark-block" },
  cvr:  { label:"Ver benchmark", scroll:"benchmark-block" },
};

// ─── Action recommendations ───────────────────────────────────────────────────
const ACTIONS = {
  tatc: [
    "CTA \"Añadir al carrito\" sticky y visible sin scroll en móvil",
    "Above-the-fold: precio final, stock, envío estimado y fecha entrega",
    "Imágenes WebP/AVIF optimizadas + vídeo corto del producto",
    "Reviews visibles: media de valoración + número de opiniones",
    "Beneficios en 3-5 bullets clave (los más diferenciales primero)",
    "Guía de compatibilidad (modelo/año/versión) para reducir dudas en recambios",
    "FAQ colapsada con las 3-5 preguntas más frecuentes del producto",
    "Trust badges: devolución, pago seguro, envío rápido",
  ],
  a2c: [
    "Mini-cart lateral con preview + botón \"Finalizar compra\" visible",
    "Barra de progreso hacia envío gratuito si aplica",
    "Costes de envío visibles en carrito (sin sorpresas en checkout)",
    "Upsells 1-clic ligeros y relevantes (complementarios, no agresivos)",
    "Trust signals: garantía, política de devoluciones, métodos de pago",
    "Persistencia del carrito cross-device y cross-session",
    "Cupón colapsado accesible (no distraer, pero presente)",
  ],
  c2b: [
    "Guest checkout sin registro obligatorio previo",
    "Apple Pay / Google Pay / PayPal visibles en la parte superior",
    "Autocompletar dirección (Google Places o nativo del navegador)",
    "Validación inline del formulario en tiempo real (no solo al enviar)",
    "Resumen de pedido fijo en desktop / desplegable en móvil",
    "Coste total con impuestos y envío desde el primer paso",
    "Fecha de entrega estimada al seleccionar método de envío",
    "3DS bien calibrado para reducir falsos declines",
    "Teléfono/WhatsApp de soporte visible durante todo el checkout",
  ],
  cvr: [
    "Core Web Vitals: LCP < 2.5s, CLS < 0.1, TTFB < 0.8s en móvil",
    "Búsqueda interna con filtros potentes (modelo, año, marca, compatibilidad)",
    "Recuperación de carrito abandonado: email automático 1h + 24h",
    "Retargeting dinámico sobre ficha de producto visualizada",
    "Programa de fidelización o descuento en segunda compra",
  ],
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtEUR = (v) => new Intl.NumberFormat("es-ES",{style:"currency",currency:"EUR",maximumFractionDigits:0}).format(v||0);
const fmtInt = (v) => new Intl.NumberFormat("es-ES",{maximumFractionDigits:0}).format(Math.round(v||0));
const pctFmt = (v) => (v==null?"–":`${Number(v).toFixed(2)}%`);
const clamp  = (v,min=0,max=100) => Math.min(max,Math.max(min,v));

function getStatus(val, rng, buf) {
  if (val>=rng[0]) return "ok";
  if (val>=rng[0]-buf) return "warn";
  return "bad";
}

function compute(sessions, trafAtc, atcCo, coBuy, aov, marginPct) {
  const atc = sessions*(trafAtc/100);
  const checkouts = atc*(atcCo/100);
  const purchases = checkouts*(coBuy/100);
  const cvr = sessions>0?(purchases/sessions)*100:0;
  const revenue = purchases*aov;
  const profit = revenue*(marginPct/100);
  return {atc,checkouts,purchases,cvr,revenue,profit};
}

function calcOpportunities(funnel, financials, sectorKey) {
  const rng = SECTORS[sectorKey];
  if (!rng) return [];
  const {sessions, traf_atc_rate:tA, atc_co_rate:aC, co_buy_rate:cB} = funnel;
  const {aov, gross_margin_pct:mgn, revenue:baseRev} = financials;
  if (!sessions||!tA||!aC||!cB) return [];

  const stages = [
    {key:"tatc", val:tA, rng:rng.tatc, buf:BUF.tatc},
    {key:"a2c",  val:aC, rng:rng.a2c,  buf:BUF.a2c },
    {key:"c2b",  val:cB, rng:rng.c2b,  buf:BUF.c2b },
  ];

  const opps = [];
  stages.forEach(({key,val,rng:r,buf})=>{
    const gap = Math.max(0, r[0]-val);
    if (gap===0) return;
    const benchMin = r[0];
    let improved;
    if (key==="tatc") improved = sessions*(benchMin/100)*(aC/100)*(cB/100);
    if (key==="a2c")  improved = sessions*(tA/100)*(benchMin/100)*(cB/100);
    if (key==="c2b")  improved = sessions*(tA/100)*(aC/100)*(benchMin/100);
    const current = sessions*(tA/100)*(aC/100)*(cB/100);
    const extraPurchases = Math.max(0,improved-current);
    const extraRevenue   = extraPurchases*aov;
    const extraProfit    = extraRevenue*(mgn/100);
    const impactPct = baseRev>0?(extraProfit/baseRev)*100:0;
    let priority;
    if ((gap>3&&impactPct>10)||gap>5) priority="ALTA";
    else if (gap>1.5||impactPct>5)   priority="MEDIA";
    else                              priority="BAJA";
    opps.push({key,gap,currentVal:val,benchmarkMin:benchMin,benchmarkRange:r,extraPurchases,extraRevenue,extraProfit,priority,status:getStatus(val,r,buf)});
  });

  opps.sort((a,b)=>b.extraProfit-a.extraProfit);
  opps.forEach((o,i)=>{
    if (i===0)       o.label="Mayor oportunidad detectada";
    else if (o.gap<=2) o.label="Quick win";
    else if (o.priority==="ALTA") o.label="Impacto alto";
    else              o.label="Optimización incremental";
  });
  return opps;
}

function calcFunnelScore(funnel, sectorKey) {
  const rng = SECTORS[sectorKey];
  if (!rng||!funnel) return {score:0,label:"Sin datos",color:"slate",worstMetric:null};
  const W = {tatc:0.20,a2c:0.30,c2b:0.35,cvr:0.15};
  const metrics = [
    {key:"tatc",val:funnel.traf_atc_rate,rng:rng.tatc,buf:BUF.tatc},
    {key:"a2c", val:funnel.atc_co_rate,  rng:rng.a2c, buf:BUF.a2c },
    {key:"c2b", val:funnel.co_buy_rate,  rng:rng.c2b, buf:BUF.c2b },
    {key:"cvr", val:funnel.overall_cvr,  rng:rng.cvr, buf:BUF.cvr },
  ];
  let total=0, worstMetric=null, worstGap=0;
  metrics.forEach(({key,val,rng:r,buf})=>{
    let c;
    if (val==null)      c=50;
    else if (val>=r[1]) c=100;
    else if (val>=r[0]) c=70+(val-r[0])/(r[1]-r[0])*30;
    else if (val>=r[0]-buf) c=40+(val-(r[0]-buf))/buf*30;
    else c=Math.max(0,40*val/(r[0]-buf||1));
    total+=c*W[key];
    const gap=val!=null?Math.max(0,r[0]-val):0;
    if (gap>worstGap){worstGap=gap;worstMetric={key,val,gap,label:STAGE_LABELS[key]};}
  });
  const score=Math.round(total);
  let label,color;
  if (score>=90){label="Excelente";color="emerald";}
  else if (score>=70){label="Bueno";color="blue";}
  else if (score>=50){label="Mejorable";color="amber";}
  else if (score>=30){label="Necesita atención";color="orange";}
  else {label="Crítico";color="red";}
  return {score,label,color,worstMetric};
}

function calcTotalOpp(opportunities) {
  return opportunities.reduce((a,o)=>({
    extraRevenue:  a.extraRevenue  + (o.extraRevenue  ||0),
    extraProfit:   a.extraProfit   + (o.extraProfit   ||0),
    extraPurchases:a.extraPurchases+ (o.extraPurchases||0),
  }),{extraRevenue:0,extraProfit:0,extraPurchases:0});
}

// ─── Revenue Leak Calculation ─────────────────────────────────────────────────
function calcRevenueLeak(funnel, financials, sectorKey) {
  const rng = SECTORS[sectorKey];
  if (!rng || !funnel || !financials) return [];
  const { sessions, add_to_cart, begin_checkout, traf_atc_rate, atc_co_rate, co_buy_rate } = funnel;
  const { aov, gross_margin_pct } = financials;
  if (!sessions || !aov) return [];

  // Derive current values consistently from rates (avoids GA4 vs BI mismatch on final step)
  const atc_cur     = sessions * ((traf_atc_rate || 0) / 100);
  const co_cur      = (add_to_cart || 0) * ((atc_co_rate || 0) / 100);
  const purch_cur   = (begin_checkout || 0) * ((co_buy_rate || 0) / 100);

  const stages = [
    { key:"tatc", label:"Tráfico → ATC",     prev:sessions,             cur:atc_cur,   rate:traf_atc_rate, bmin:rng.tatc[0] },
    { key:"a2c",  label:"ATC → Checkout",    prev:add_to_cart||0,       cur:co_cur,    rate:atc_co_rate,   bmin:rng.a2c[0]  },
    { key:"c2b",  label:"Checkout → Compra", prev:begin_checkout||0,    cur:purch_cur, rate:co_buy_rate,   bmin:rng.c2b[0]  },
  ];

  const leaks = [];
  stages.forEach(({ key, label, prev, cur, rate, bmin }) => {
    if (rate == null || rate >= bmin || !prev) return;
    const expected = prev * (bmin / 100);
    const lost_units = Math.max(0, expected - cur);
    const revenue_lost = lost_units * aov;
    const profit_lost = gross_margin_pct > 0 ? lost_units * aov * (gross_margin_pct / 100) : null;
    const gap_pp = bmin - rate;
    leaks.push({ key, label, current_rate:rate, benchmark_min:bmin, gap_pp, lost_units:Math.round(lost_units), revenue_lost, profit_lost });
  });

  leaks.sort((a, b) => b.revenue_lost - a.revenue_lost);
  return leaks;
}

// ─── Experiments constants ─────────────────────────────────────────────────────
const CHANGE_TYPE_LABELS = {
  cart:             "Carrito",
  checkout:         "Checkout",
  product_page:     "Página de producto",
  pricing:          "Precios",
  shipping:         "Envío",
  payment_methods:  "Métodos de pago",
  theme_change:     "Tema / Diseño",
  marketing:        "Marketing",
  other:            "Otro",
};

function calcStageImpact(stageKey, benchMin, funnel, financials) {
  const {sessions,traf_atc_rate:tA,atc_co_rate:aC,co_buy_rate:cB} = funnel;
  const {aov,gross_margin_pct:mgn} = financials;
  if (!sessions||!aov) return null;
  const cur = {tatc:tA,a2c:aC,c2b:cB}[stageKey];
  if (cur==null||cur>=benchMin) return null;
  let improved;
  if (stageKey==="tatc") improved=sessions*(benchMin/100)*(aC/100)*(cB/100);
  if (stageKey==="a2c")  improved=sessions*(tA/100)*(benchMin/100)*(cB/100);
  if (stageKey==="c2b")  improved=sessions*(tA/100)*(aC/100)*(benchMin/100);
  const current=sessions*(tA/100)*(aC/100)*(cB/100);
  const extra=Math.max(0,improved-current);
  return {extraRevenue:extra*aov, extraProfit:extra*aov*(mgn/100)};
}

function getSensHighlight(base){
  const {sessions,traf_atc_rate:tA,atc_co_rate:aC,co_buy_rate:cB}=base?.funnel||{};
  const {aov,gross_margin_pct:mgn}=base?.financials||{};
  if (!sessions||!aov) return null;
  const d=2;
  const aCN=clamp((aC||0)+d),cBN=clamp((cB||0)+d);
  const bR=compute(sessions,tA||0,aC||0,cB||0,aov,mgn||0).revenue;
  const sR=compute(sessions,tA||0,aCN,cBN,aov,mgn||0).revenue;
  const delta=sR-bR;
  return delta>0?fmtEUR(delta):null;
}

// ─── Components ───────────────────────────────────────────────────────────────
const PriorityBadge = ({priority})=>{
  const cfg={ALTA:"bg-red-100 text-red-800 border-red-200",MEDIA:"bg-amber-100 text-amber-800 border-amber-200",BAJA:"bg-slate-100 text-slate-600 border-slate-200"};
  return <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-bold border ${cfg[priority]||cfg.BAJA}`}>{priority}</span>;
};

const StatusPill = ({status})=>{
  const cfg={ok:{cls:"bg-emerald-100 text-emerald-800 border-emerald-200",txt:"Dentro del sector"},warn:{cls:"bg-amber-100 text-amber-800 border-amber-200",txt:"Cerca del sector"},bad:{cls:"bg-red-100 text-red-800 border-red-200",txt:"Por debajo"}};
  const c=cfg[status]||cfg.bad;
  return <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${c.cls}`}>{c.txt}</span>;
};

// ── Funnel Score Panel ──
const FunnelScorePanel = ({scoreData, totalOpp, ga4HasData})=>{
  const {score,label,color,worstMetric}=scoreData;
  const barCls={"emerald":"bg-emerald-500","blue":"bg-blue-500","amber":"bg-amber-500","orange":"bg-orange-500","red":"bg-red-500","slate":"bg-slate-300"}[color]||"bg-slate-300";
  const textCls={"emerald":"text-emerald-600","blue":"text-blue-600","amber":"text-amber-600","orange":"text-orange-600","red":"text-red-600","slate":"text-slate-500"}[color]||"text-slate-500";
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="funnel-score-panel">
      <Card className="lg:col-span-1">
        <CardContent className="p-5">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Funnel Score</p>
          <div className="flex items-baseline gap-2 mb-2">
            <span className={`text-5xl font-black tabular-nums ${textCls}`} data-testid="funnel-score-value">{score}</span>
            <span className="text-xl text-slate-400 font-semibold">/ 100</span>
          </div>
          <div className="w-full h-2.5 bg-slate-100 rounded-full mb-2 overflow-hidden">
            <div className={`h-2.5 rounded-full ${barCls} transition-all duration-700`} style={{width:`${score}%`}}/>
          </div>
          <p className={`text-sm font-bold ${textCls}`}>{label}</p>
          {worstMetric&&worstMetric.gap>0&&(
            <p className="text-xs text-slate-500 mt-2">
              Principal problema: <strong className="text-red-600">{worstMetric.label} (-{worstMetric.gap.toFixed(1)}pp)</strong>
            </p>
          )}
        </CardContent>
      </Card>
      {ga4HasData&&totalOpp?.extraRevenue>0?(
        <Card className="lg:col-span-2 bg-gradient-to-br from-slate-800 to-slate-900 text-white border-0" data-testid="total-opportunity-card">
          <CardContent className="p-5">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Oportunidad total detectada</p>
            <p className="text-xs text-slate-500 mb-4">Estimado al alcanzar el benchmark mínimo en cada etapa del funnel</p>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-slate-400 mb-1">Compras adicionales</p>
                <p className="text-2xl font-black text-white">+{fmtInt(totalOpp.extraPurchases)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-1">Revenue potencial</p>
                <p className="text-2xl font-black text-blue-300">+{fmtEUR(totalOpp.extraRevenue)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-1">Profit potencial</p>
                <p className="text-2xl font-black text-emerald-300">+{fmtEUR(totalOpp.extraProfit)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      ):(
        <Card className="lg:col-span-2 border-dashed border-slate-200 bg-slate-50">
          <CardContent className="p-5 flex items-center justify-center h-full">
            <p className="text-sm text-slate-400">
              {ga4HasData?"Tu funnel está dentro del benchmark en todas las etapas.":"Conecta GA4 para calcular la oportunidad total."}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ── Top Opportunity Card ──
const TopOpportunityCard = ({opportunities})=>{
  if (!opportunities||opportunities.length===0) return null;
  const top=opportunities[0];
  if (!top) return null;
  const cta=STAGE_CTA[top.key];
  return (
    <div className="rounded-xl border border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 p-4 flex flex-wrap items-start gap-4"
      data-testid="top-opportunity-card">
      <div className="flex-shrink-0 w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
        <Zap className="w-5 h-5 text-white"/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <p className="text-xs font-bold text-orange-700 uppercase tracking-wider">Top Opportunity Detected</p>
          <PriorityBadge priority={top.priority}/>
        </div>
        <p className="text-sm font-bold text-slate-800 mb-2">
          {STAGE_LABELS[top.key]} está <span className="text-red-600">{top.gap.toFixed(1)}pp</span> por debajo del benchmark mínimo
        </p>
        <div className="flex flex-wrap gap-6">
          <div><p className="text-xs text-slate-500">Revenue potencial</p><p className="text-lg font-black text-blue-700">+{fmtEUR(top.extraRevenue)}</p></div>
          <div><p className="text-xs text-slate-500">Profit potencial</p><p className="text-lg font-black text-emerald-700">+{fmtEUR(top.extraProfit)}</p></div>
          <div><p className="text-xs text-slate-500">Compras adicionales</p><p className="text-lg font-black text-slate-700">+{fmtInt(top.extraPurchases)}</p></div>
        </div>
      </div>
      {cta&&!cta.scroll&&(
        <Link to={cta.to} className="flex-shrink-0 self-center">
          <Button size="sm" variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-100 gap-1">
            {cta.label}<ArrowRight className="w-3 h-3"/>
          </Button>
        </Link>
      )}
    </div>
  );
};

// ── Bloque 1 – Funnel Real ──
const FunnelBlock = ({funnel, financials, ga4Connected, ga4HasEvents, sectorKey})=>{
  const rng = SECTORS[sectorKey];
  const noGA4=!ga4Connected, noEvents=ga4Connected&&!ga4HasEvents;

  const steps = [
    {label:"Sesiones",  value:funnel?.sessions,       pctBar:100,          rate:null,                  stageKey:null,  avail:ga4Connected},
    {label:"ATC",       value:funnel?.add_to_cart,    pctBar:funnel?.sessions>0?funnel.add_to_cart/funnel.sessions*100:0,  rate:funnel?.traf_atc_rate, stageKey:"tatc",avail:ga4Connected&&ga4HasEvents},
    {label:"Checkout",  value:funnel?.begin_checkout, pctBar:funnel?.sessions>0?funnel.begin_checkout/funnel.sessions*100:0,rate:funnel?.atc_co_rate, stageKey:"a2c", avail:ga4Connected&&ga4HasEvents},
    {label:"Compras",   value:funnel?.orders,         pctBar:funnel?.sessions>0?funnel.orders/funnel.sessions*100:0,       rate:funnel?.co_buy_rate,  stageKey:"c2b", avail:true},
  ];

  const COLORS=["bg-indigo-500","bg-violet-500","bg-amber-500","bg-emerald-500"];
  const COLORS_LIGHT=["bg-indigo-50","bg-violet-50","bg-amber-50","bg-emerald-50"];
  const COLORS_TEXT=["text-indigo-700","text-violet-700","text-amber-700","text-emerald-700"];

  const getRateStyle=(stageKey,val)=>{
    if (!rng||val==null) return {cls:"text-slate-400 bg-slate-50 border-slate-200",status:null};
    const sr=stageKey==="tatc"?rng.tatc:stageKey==="a2c"?rng.a2c:rng.c2b;
    const sb=stageKey==="tatc"?BUF.tatc:stageKey==="a2c"?BUF.a2c:BUF.c2b;
    const st=getStatus(val,sr,sb);
    if (st==="ok")   return {cls:"text-emerald-700 bg-emerald-50 border-emerald-200 font-black",status:"ok",tag:"dentro del benchmark"};
    if (st==="warn") return {cls:"text-amber-700 bg-amber-50 border-amber-200 font-black",status:"warn",tag:"cerca del benchmark"};
    return {cls:"text-red-700 bg-red-50 border-red-200 font-black",status:"bad",tag:"por debajo del benchmark"};
  };

  return (
    <Card data-testid="funnel-block">
      <CardContent className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <h2 className="text-lg font-bold">Tu Funnel Real</h2>
          {noGA4&&(
            <div className="flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-1.5" data-testid="ga4-warning">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0"/>
              Sin GA4 conectado — tasas del funnel no disponibles.{" "}
              <Link to="/integrations" className="underline font-semibold">Conectar</Link>
            </div>
          )}
          {noEvents&&(
            <div className="flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-1.5">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0"/>
              GA4 conectado sin eventos e-commerce configurados.
            </div>
          )}
        </div>

        {/* Funnel steps – centered proportional funnel */}
        <div className="space-y-0 mb-6">
          {steps.map((step,i)=>{
            const rs = step.stageKey ? getRateStyle(step.stageKey, step.rate) : null;
            const stageRng = step.stageKey&&rng?(step.stageKey==="tatc"?rng.tatc:step.stageKey==="a2c"?rng.a2c:rng.c2b):null;
            // Sqrt-scale widths relative to sessions → proper funnel taper
            const sessions = funnel?.sessions || 0;
            const rawPct = step.pctBar || 0;
            const barW = i === 0
              ? 100
              : sessions > 0 && rawPct > 0
                ? Math.max(Math.sqrt(rawPct / 100) * 100, 10)
                : Math.max(100 - i * 22, 30); // graceful fallback
            return (
              <div key={step.label} className="flex flex-col items-center">
                {i>0&&(
                  <div className="flex flex-col items-center py-1.5 w-full">
                    <div className="w-0.5 h-4 bg-slate-200"/>
                    {step.avail&&step.rate!=null&&rs?(
                      <div className={`flex items-center gap-3 px-3 py-2 rounded-xl border ${rs.status==="ok"?"border-emerald-200 bg-emerald-50":rs.status==="warn"?"border-amber-200 bg-amber-50":"border-red-200 bg-red-50"}`}>
                        <span className={`text-xl font-black tabular-nums ${rs.status==="ok"?"text-emerald-700":rs.status==="warn"?"text-amber-700":"text-red-700"}`}>
                          ↓ {pctFmt(step.rate)}
                        </span>
                        {stageRng&&(
                          <div className="text-xs pl-3 border-l border-slate-200">
                            <p className="text-slate-400">Benchmark: {stageRng[0]}–{stageRng[1]}%</p>
                            {rs.status!=="ok"&&(
                              <p className={`font-bold ${rs.status==="warn"?"text-amber-600":"text-red-600"}`}>
                                Gap: {(step.rate-stageRng[0]).toFixed(2)}pp
                              </p>
                            )}
                          </div>
                        )}
                        <StatusPill status={rs.status}/>
                      </div>
                    ):(
                      <div className="px-3 py-2 rounded-xl border border-slate-200 bg-slate-50">
                        <span className="text-xl font-black text-slate-400">↓ —</span>
                      </div>
                    )}
                    <div className="w-0.5 h-4 bg-slate-200"/>
                  </div>
                )}
                <div style={{width:`${barW}%`, maxWidth:"100%"}}
                     className={`${COLORS[i]} rounded-xl px-5 py-4 flex items-center justify-between transition-all duration-700`}>
                  <div className="flex items-center gap-3">
                    <span className="text-white font-black text-2xl tabular-nums">
                      {step.avail&&step.value!=null?fmtInt(step.value):"–"}
                    </span>
                    <span className="text-white/75 text-sm font-semibold">{step.label}</span>
                  </div>
                  {step.avail&&step.value!=null&&step.pctBar>0&&(
                    <div className={`text-xs px-2 py-0.5 rounded-full ${COLORS_LIGHT[i]} ${COLORS_TEXT[i]} font-semibold`}>
                      {step.pctBar.toFixed(1)}%
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* KPI row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-4 border-t border-slate-100">
          {[
            {label:"Conversión total", value:funnel?.overall_cvr!=null&&ga4Connected&&ga4HasEvents?pctFmt(funnel.overall_cvr):"–"},
            {label:"AOV",              value:financials?.aov?fmtEUR(financials.aov):"–"},
            {label:"Revenue",          value:financials?.revenue?fmtEUR(financials.revenue):"–"},
            {label:"Profit estimado",  value:financials?.gross_profit?fmtEUR(financials.gross_profit):"–"},
          ].map(kpi=>(
            <div key={kpi.label} className="text-center">
              <p className="text-xs text-slate-500 font-medium mb-0.5">{kpi.label}</p>
              <p className="text-lg font-black text-slate-800">{kpi.value}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// ── Bloque 2 – Benchmark ──
const BenchmarkBlock = ({funnel, financials, sectorKey, ga4HasData})=>{
  const rng = SECTORS[sectorKey];
  if (!rng) return null;

  const metrics=[
    {key:"tatc",label:"Tráfico → ATC",   val:funnel?.traf_atc_rate, rng:rng.tatc, buf:BUF.tatc, stageKey:"tatc"},
    {key:"a2c", label:"ATC → Checkout",  val:funnel?.atc_co_rate,   rng:rng.a2c,  buf:BUF.a2c,  stageKey:"a2c"},
    {key:"c2b", label:"Checkout → Compra",val:funnel?.co_buy_rate,   rng:rng.c2b,  buf:BUF.c2b,  stageKey:"c2b"},
    {key:"cvr", label:"Conversión total", val:funnel?.overall_cvr,   rng:rng.cvr,  buf:BUF.cvr,  stageKey:null},
  ];

  return (
    <Card data-testid="benchmark-block" id="benchmark-block">
      <CardContent className="p-6">
        <h2 className="text-lg font-bold mb-4">Benchmark por Sector — {SECTORS[sectorKey]?.label}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {metrics.map(({key,label,val,rng:r,buf,stageKey})=>{
            const avail=(ga4HasData)||(key==="cvr"&&ga4HasData);
            const status=avail&&val!=null?getStatus(val,r,buf):null;
            const impact = avail&&val!=null&&stageKey&&funnel&&financials
              ? calcStageImpact(stageKey,r[0],funnel,financials) : null;
            return (
              <div key={key} className="border border-slate-100 rounded-xl p-4">
                <p className="text-xs font-semibold text-slate-500 mb-2">{label}</p>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xl font-black text-slate-800">{avail&&val!=null?pctFmt(val):"–"}</span>
                  {status&&<StatusPill status={status}/>}
                </div>
                <p className="text-xs text-slate-400 mb-1">Benchmark: {r[0]}–{r[1]}%</p>
                {avail&&val!=null&&(
                  <p className={`text-xs font-bold mb-2 ${status==="ok"?"text-emerald-600":status==="warn"?"text-amber-600":"text-red-600"}`}>
                    {status==="ok"?"Dentro del benchmark":`Gap: ${(val-r[0]).toFixed(2)}pp`}
                  </p>
                )}
                {impact&&(
                  <div className="bg-red-50 rounded-lg p-2 text-xs border border-red-100">
                    <p className="text-red-600 font-semibold mb-0.5">Oportunidad potencial</p>
                    <p className="text-red-700">+{fmtEUR(impact.extraRevenue)} revenue</p>
                    <p className="text-red-700">+{fmtEUR(impact.extraProfit)} profit</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// ── Bloque 3 – Opportunities ──
const OpportunitiesBlock = ({opportunities})=>{
  if (!opportunities||opportunities.length===0){
    return (
      <Card data-testid="opportunities-block">
        <CardContent className="p-6 text-center">
          <TrendingUp className="w-8 h-8 text-emerald-400 mx-auto mb-2"/>
          <p className="font-semibold text-slate-700">¡Tu funnel está por encima del benchmark!</p>
          <p className="text-sm text-slate-500 mt-1">No se detectan oportunidades significativas en este período.</p>
        </CardContent>
      </Card>
    );
  }
  const priorityIcon={ALTA:"🔥",MEDIA:"⚡",BAJA:"💡"};
  return (
    <Card data-testid="opportunities-block">
      <CardContent className="p-6">
        <h2 className="text-lg font-bold mb-4">Oportunidades Detectadas</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {opportunities.map(opp=>(
            <div key={opp.key} className="border border-slate-100 rounded-xl p-4 flex flex-col gap-3" data-testid={`opportunity-${opp.key}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">{STAGE_LABELS[opp.key]}</p>
                  <p className="text-sm font-bold text-slate-800 mt-0.5">{priorityIcon[opp.priority]} {opp.label}</p>
                </div>
                <PriorityBadge priority={opp.priority}/>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-xs space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-slate-500">Actual</span>
                  <span className="font-bold text-slate-700">{pctFmt(opp.currentVal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Benchmark mínimo</span>
                  <span className="font-bold text-slate-700">{pctFmt(opp.benchmarkMin)}</span>
                </div>
                <div className="flex justify-between text-amber-700">
                  <span>Gap</span>
                  <span className="font-bold">{opp.gap.toFixed(2)} pp</span>
                </div>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Compras adicionales</span>
                  <span className="font-bold">+{fmtInt(opp.extraPurchases)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Revenue adicional</span>
                  <span className="font-bold text-blue-700">+{fmtEUR(opp.extraRevenue)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Profit adicional</span>
                  <span className="font-bold text-emerald-700">+{fmtEUR(opp.extraProfit)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// ── Bloque 4 – Simulator ──
const SimulatorBlock = ({base, isOpen, onToggle, externalDeltas})=>{
  const [deltas, setDeltas] = useState({sessions:0,trafAtc:0,atcCo:0,coBuy:0,aov:0,margin:0});
  const setD = (k,v)=>setDeltas(d=>({...d,[k]:parseFloat(v)||0}));

  useEffect(()=>{
    if (externalDeltas) setDeltas(d=>({...d,atcCo:externalDeltas.atcCo||0,coBuy:externalDeltas.coBuy||0}));
  },[externalDeltas]);

  const bp={
    sessions:base?.funnel?.sessions||0,
    trafAtc:base?.funnel?.traf_atc_rate||0,
    atcCo:base?.funnel?.atc_co_rate||0,
    coBuy:base?.funnel?.co_buy_rate||0,
    aov:base?.financials?.aov||0,
    margin:base?.financials?.gross_margin_pct||0,
  };
  const sp={
    sessions:bp.sessions+deltas.sessions,
    trafAtc:clamp(bp.trafAtc+deltas.trafAtc),
    atcCo:clamp(bp.atcCo+deltas.atcCo),
    coBuy:clamp(bp.coBuy+deltas.coBuy),
    aov:bp.aov+deltas.aov,
    margin:clamp(bp.margin+deltas.margin),
  };
  const bC=compute(bp.sessions,bp.trafAtc,bp.atcCo,bp.coBuy,bp.aov,bp.margin);
  const sC=compute(sp.sessions,sp.trafAtc,sp.atcCo,sp.coBuy,sp.aov,sp.margin);

  const fields=[
    {key:"sessions",label:"Δ Sesiones (uds)",step:100},
    {key:"trafAtc", label:"Δ Tráfico→ATC (pp)",step:0.1},
    {key:"atcCo",   label:"Δ ATC→Checkout (pp)",step:0.1},
    {key:"coBuy",   label:"Δ Checkout→Compra (pp)",step:0.1},
    {key:"aov",     label:"Δ AOV (€)",step:1},
    {key:"margin",  label:"Δ Margen (pp)",step:0.1},
  ];

  return (
    <Card data-testid="simulator-block">
      <CardContent className="p-0">
        <button onClick={onToggle}
          className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors rounded-xl"
          data-testid="simulator-toggle">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-violet-500"/>
            <span className="text-lg font-bold">Simulador Manual</span>
            <span className="text-xs text-slate-500 hidden sm:block">— ajusta los Δ para ver el impacto</span>
          </div>
          {isOpen?<ChevronUp className="w-5 h-5 text-slate-400"/>:<ChevronDown className="w-5 h-5 text-slate-400"/>}
        </button>
        {isOpen&&(
          <div className="px-6 pb-6 border-t border-slate-100">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6 pt-4">
              {fields.map(f=>(
                <div key={f.key}>
                  <label className="text-xs text-slate-500 font-semibold block mb-1">{f.label}</label>
                  <input type="number" step={f.step} value={deltas[f.key]}
                    onChange={e=>setD(f.key,e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-300"
                    data-testid={`sim-delta-${f.key}`}/>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[{label:"Base",bg:"bg-slate-50"},{label:"Simulado",bg:"bg-violet-50"},{label:"Impacto (Δ)",bg:"bg-emerald-50"}].map(({label,bg})=>(
                <div key={label} className={`${bg} rounded-xl p-4`}>
                  <p className="text-xs font-bold text-slate-600 mb-3 uppercase tracking-wider">{label}</p>
                  <div className="space-y-2">
                    {[
                      {l:"Compras",     b:fmtInt(bC.purchases),  s:fmtInt(sC.purchases),  d:`+${fmtInt(sC.purchases-bC.purchases)}`},
                      {l:"% Conversión",b:pctFmt(bC.cvr),         s:pctFmt(sC.cvr),         d:`${(sC.cvr-bC.cvr).toFixed(2)} pp`},
                      {l:"Revenue",     b:fmtEUR(bC.revenue),    s:fmtEUR(sC.revenue),    d:fmtEUR(sC.revenue-bC.revenue)},
                      {l:"Profit",      b:fmtEUR(bC.profit),     s:fmtEUR(sC.profit),     d:fmtEUR(sC.profit-bC.profit)},
                    ].map(({l,b,s,d})=>(
                      <div key={l}>
                        <p className="text-xs text-slate-500">{l}</p>
                        <p className="text-sm font-bold text-slate-800">
                          {label==="Base"?b:label==="Simulado"?s:(
                            <span className={sC.profit-bC.profit>=0?"text-emerald-700":"text-red-600"}>{d}</span>
                          )}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── Bloque 5 – Sensitivity ──
const SensitivityBlock = ({base, onApplyDelta})=>{
  const rows=[-4,-3,-2,-1,0,1,2,3,4];
  const cols=[-4,-3,-2,-1,0,1,2,3,4];
  const {sessions,traf_atc_rate:tA,atc_co_rate:aC,co_buy_rate:cB}=base?.funnel||{};
  const {aov,gross_margin_pct:mgn}=base?.financials||{};
  const baseRev=compute(sessions||0,tA||0,aC||0,cB||0,aov||0,mgn||0).revenue;
  const highlight=getSensHighlight(base);
  if (!sessions||!aov) return null;
  return (
    <Card data-testid="sensitivity-block">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
          <div>
            <h2 className="text-lg font-bold">Sensibilidad</h2>
            <p className="text-xs text-slate-500 mt-0.5">Impacto en Revenue (Δ pp). Haz clic en una celda para aplicar los Δ al simulador.</p>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-100 inline-block"/> Mejora</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-100 inline-block"/> Empeora</span>
          </div>
        </div>
        {highlight&&(
          <div className="mb-4 text-sm text-slate-700 bg-blue-50 rounded-lg px-4 py-3 border border-blue-100">
            Si mejoras <strong>+2pp ATC→Checkout</strong> y <strong>+2pp Checkout→Compra</strong>, podrías generar aproximadamente{" "}
            <strong className="text-emerald-700">{highlight}</strong> de revenue adicional en este período.
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse min-w-[600px]">
            <thead>
              <tr>
                <th className="text-left p-2 text-slate-400 font-semibold whitespace-nowrap">CO→Compra \ ATC→CO (pp)</th>
                {cols.map(c=><th key={c} className="p-2 text-center text-slate-600 font-semibold">{c>0?`+${c}`:c}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map(r=>(
                <tr key={r} className="border-t border-slate-100">
                  <td className="p-2 font-semibold text-slate-600 whitespace-nowrap sticky left-0 bg-white">{r>0?`+${r}`:r}</td>
                  {cols.map(c=>{
                    const aCN=clamp(aC+c), cBN=clamp(cB+r);
                    const rev=compute(sessions,tA,aCN,cBN,aov,mgn).revenue;
                    const delta=rev-baseRev;
                    const isBase=r===0&&c===0;
                    return (
                      <td key={c} onClick={()=>onApplyDelta&&onApplyDelta({atcCo:c,coBuy:r})}
                        title={`Δ ATC→CO: ${c>0?"+":""}${c}pp, Δ CO→Buy: ${r>0?"+":""}${r}pp`}
                        className={`p-2 text-center font-mono font-semibold cursor-pointer rounded transition-opacity hover:opacity-75
                          ${isBase?"bg-slate-200 text-slate-600":delta>=0?"bg-emerald-50 text-emerald-800":"bg-red-50 text-red-700"}`}
                        data-testid={`sens-cell-${r}-${c}`}>
                        {delta>=0?"+":""}{fmtEUR(delta)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// ── Bloque 6 – Actions ──
const ActionsBlock = ({opportunities})=>{
  const actionOpps=opportunities.filter(o=>o.status!=="ok");
  if (!actionOpps.length) return null;
  return (
    <Card data-testid="actions-block">
      <CardContent className="p-6">
        <h2 className="text-lg font-bold mb-4">Acciones Recomendadas</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {actionOpps.map(opp=>{
            const actions=ACTIONS[opp.key]||[];
            const cta=STAGE_CTA[opp.key];
            return (
              <div key={opp.key} className="border border-slate-100 rounded-xl p-4 flex flex-col gap-3" data-testid={`action-block-${opp.key}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-base font-bold text-slate-800">{STAGE_LABELS[opp.key]}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Actual: <strong>{pctFmt(opp.currentVal)}</strong> — Benchmark mín: <strong>{pctFmt(opp.benchmarkMin)}</strong> — Gap: <strong className="text-amber-600">{opp.gap.toFixed(2)} pp</strong>
                    </p>
                  </div>
                  <StatusPill status={opp.status}/>
                </div>
                <div className="flex gap-4 text-xs bg-slate-50 rounded-lg p-2.5">
                  <div><span className="text-slate-400">Δ Revenue </span><span className="font-bold text-blue-700">+{fmtEUR(opp.extraRevenue)}</span></div>
                  <div><span className="text-slate-400">Δ Profit </span><span className="font-bold text-emerald-700">+{fmtEUR(opp.extraProfit)}</span></div>
                </div>
                <ul className="space-y-1.5">
                  {actions.map((a,i)=>(
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <ChevronRight className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0"/>{a}
                    </li>
                  ))}
                </ul>
                <div className="pt-1 border-t border-slate-100">
                  {cta?.scroll?(
                    <button onClick={()=>document.getElementById(cta.scroll)?.scrollIntoView({behavior:"smooth"})}
                      className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-800 font-semibold transition-colors">
                      {cta.label}<ExternalLink className="w-3 h-3"/>
                    </button>
                  ):(
                    <Link to={cta?.to||"#"} className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-800 font-semibold transition-colors">
                      {cta?.label}<ExternalLink className="w-3 h-3"/>
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// ── Revenue Leak Block ──
const RevenueLeakBlock = ({funnel, financials, sectorKey, ga4HasData})=>{
  if (!ga4HasData) return null;
  const leaks = calcRevenueLeak(funnel, financials, sectorKey);
  if (!leaks || leaks.length === 0) return null;

  return (
    <Card className="border-red-200 bg-gradient-to-br from-red-50 to-orange-50" data-testid="revenue-leak-block">
      <CardContent className="p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 bg-red-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <DollarSign className="w-5 h-5 text-white"/>
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-800">Revenue Leaks Detectados</h2>
            <p className="text-xs text-slate-500 mt-0.5">Dinero perdido en cada etapa del funnel por estar por debajo del benchmark mínimo</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {leaks.map(leak=>(
            <div key={leak.key} className="bg-white rounded-xl border border-red-200 p-4 flex flex-col gap-3" data-testid={`leak-${leak.key}`}>
              <div>
                <p className="text-xs font-bold text-red-700 uppercase tracking-wider">{leak.label}</p>
                <p className="text-xs text-slate-500 mt-0.5">{fmtInt(leak.lost_units)} unidades perdidas por debajo del benchmark</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 space-y-1.5 text-xs">
                <div className="flex justify-between">
                  <span className="text-slate-500">Tasa actual</span>
                  <span className="font-bold text-red-600">{pctFmt(leak.current_rate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Benchmark mínimo</span>
                  <span className="font-bold text-slate-700">{pctFmt(leak.benchmark_min)}</span>
                </div>
                <div className="flex justify-between border-t border-slate-200 pt-1.5">
                  <span className="text-amber-600 font-semibold">Gap</span>
                  <span className="font-bold text-amber-700">−{leak.gap_pp.toFixed(2)} pp</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500">Revenue leak</span>
                  <span className="text-base font-black text-red-600">−{fmtEUR(leak.revenue_lost)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500">Profit leak</span>
                  <span className="text-base font-black text-red-500">
                    {leak.profit_lost != null ? `−${fmtEUR(leak.profit_lost)}` : <span className="text-slate-400 text-xs">no disponible</span>}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// ── Add Experiment Modal ──
const CHANGE_TYPES = Object.keys(CHANGE_TYPE_LABELS);

const AddExperimentModal = ({onClose, onCreate})=>{
  const today = format(new Date(), "yyyy-MM-dd");
  const [form, setForm] = useState({
    title: "",
    change_type: "checkout",
    description: "",
    created_at: today,
    evaluation_window_days: 7,
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({...f, [k]: v}));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("El título es obligatorio"); return; }
    setSaving(true);
    try {
      await onCreate(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={open=>{ if (!open) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="add-experiment-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-violet-500"/>
            Añadir Experimento
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Título *</label>
            <input
              type="text"
              value={form.title}
              onChange={e=>set("title", e.target.value)}
              placeholder="Ej: Apple Pay añadido al checkout"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
              data-testid="experiment-title-input"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Tipo de cambio</label>
            <select
              value={form.change_type}
              onChange={e=>set("change_type", e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-300"
              data-testid="experiment-type-select"
            >
              {CHANGE_TYPES.map(t=>(
                <option key={t} value={t}>{CHANGE_TYPE_LABELS[t]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Descripción (opcional)</label>
            <textarea
              value={form.description}
              onChange={e=>set("description", e.target.value)}
              placeholder="Breve descripción del cambio realizado..."
              rows={2}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-violet-300"
              data-testid="experiment-description-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Fecha del cambio</label>
              <input
                type="date"
                value={form.created_at}
                onChange={e=>set("created_at", e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
                data-testid="experiment-date-input"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Ventana evaluación (días)</label>
              <input
                type="number"
                min={1}
                max={90}
                value={form.evaluation_window_days}
                onChange={e=>set("evaluation_window_days", parseInt(e.target.value)||7)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
                data-testid="experiment-window-input"
              />
            </div>
          </div>
          <DialogFooter className="pt-1">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancelar</Button>
            <Button type="submit" size="sm" disabled={saving} data-testid="experiment-save-btn">
              {saving?<><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/>Guardando...</>:"Guardar experimento"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ── Experiment Card ──
const ExperimentCard = ({experiment, impact, onDelete})=>{
  const [confirmDelete, setConfirmDelete] = useState(false);
  const dateLabel = experiment.created_at
    ? format(parseISO(experiment.created_at.length > 10 ? experiment.created_at : experiment.created_at + "T00:00:00"), "d MMM yyyy", {locale:es})
    : "–";

  const renderImpact = ()=>{
    if (!impact) return (
      <div className="flex items-center gap-1.5 text-xs text-slate-400">
        <Loader2 className="w-3.5 h-3.5 animate-spin"/> Calculando impacto...
      </div>
    );
    if (impact.status === "future") return (
      <div className="flex items-center gap-1.5 text-xs text-blue-500">
        <Clock3 className="w-3.5 h-3.5"/> Experimento pendiente de inicio
      </div>
    );
    if (impact.status === "in_progress") return (
      <div className="flex items-center gap-1.5 text-xs text-amber-500">
        <Clock3 className="w-3.5 h-3.5"/> Evaluando — quedan {impact.days_remaining} día{impact.days_remaining!==1?"s":""} para completar
      </div>
    );
    if (impact.status === "insufficient_data" || impact.status === "error") return (
      <div className="flex items-center gap-1.5 text-xs text-slate-400">
        <XCircle className="w-3.5 h-3.5"/> Datos insuficientes para evaluar
      </div>
    );
    if (impact.status !== "calculated") return null;

    const { deltas } = impact;
    const hasGoodData = deltas.co_buy_rate != null || deltas.revenue != null;
    if (!hasGoodData) return (
      <div className="flex items-center gap-1.5 text-xs text-slate-400">
        <XCircle className="w-3.5 h-3.5"/> Sin datos de funnel para comparar
      </div>
    );

    const metrics = [
      deltas.co_buy_rate    != null && { label:"Checkout → Compra", val:deltas.co_buy_rate,    unit:"pp", isRate:true },
      deltas.atc_co_rate    != null && { label:"ATC → Checkout",    val:deltas.atc_co_rate,    unit:"pp", isRate:true },
      deltas.traf_atc_rate  != null && { label:"Tráfico → ATC",     val:deltas.traf_atc_rate,  unit:"pp", isRate:true },
      deltas.overall_cvr    != null && { label:"Conversión total",   val:deltas.overall_cvr,    unit:"pp", isRate:true },
      deltas.revenue        != null && { label:"Revenue",            val:deltas.revenue,        unit:"€",  isRate:false },
      deltas.gross_profit   != null && { label:"Profit",             val:deltas.gross_profit,   unit:"€",  isRate:false },
    ].filter(Boolean);

    return (
      <div className="flex flex-wrap gap-2 mt-1">
        {metrics.map(m=>{
          const isPos = m.val >= 0;
          const display = m.isRate
            ? `${isPos?"+":""}${m.val.toFixed(2)} pp`
            : `${isPos?"+":""}${fmtEUR(m.val)}`;
          return (
            <div key={m.label} className={`rounded-lg px-2.5 py-1.5 text-xs border ${isPos?"bg-emerald-50 border-emerald-200 text-emerald-800":"bg-red-50 border-red-200 text-red-700"}`}>
              <span className="text-slate-500 mr-1">{m.label}</span>
              <span className="font-bold">{display}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="border border-slate-200 rounded-xl p-4 hover:border-slate-300 transition-colors bg-white" data-testid={`experiment-card-${experiment.experiment_id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-start gap-3 min-w-0">
          <div className="flex-shrink-0 mt-0.5">
            <CheckCircle2 className="w-4 h-4 text-violet-400"/>
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-0.5">
              <span className="text-xs text-slate-400 font-medium">{dateLabel}</span>
              <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-medium">
                {CHANGE_TYPE_LABELS[experiment.change_type] || experiment.change_type}
              </span>
            </div>
            <p className="font-bold text-slate-800 text-sm">{experiment.title}</p>
            {experiment.description && (
              <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{experiment.description}</p>
            )}
          </div>
        </div>
        <div className="flex-shrink-0">
          {confirmDelete ? (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-red-600">¿Eliminar?</span>
              <button onClick={onDelete}
                className="text-xs text-white bg-red-500 hover:bg-red-600 px-2 py-1 rounded font-semibold transition-colors"
                data-testid={`experiment-confirm-delete-${experiment.experiment_id}`}>
                Sí
              </button>
              <button onClick={()=>setConfirmDelete(false)} className="text-xs text-slate-500 hover:text-slate-700 px-1">No</button>
            </div>
          ) : (
            <button onClick={()=>setConfirmDelete(true)}
              className="text-slate-300 hover:text-red-400 transition-colors p-1"
              data-testid={`experiment-delete-${experiment.experiment_id}`}>
              <Trash2 className="w-4 h-4"/>
            </button>
          )}
        </div>
      </div>
      <div className="ml-7">
        {renderImpact()}
      </div>
    </div>
  );
};

// ── Experiments History Block ──
const ExperimentsHistory = ()=>{
  const [experiments, setExperiments] = useState([]);
  const [impacts, setImpacts] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchImpact = async (exp) => {
    try {
      const r = await axios.get(`${API}/experiments/${exp.experiment_id}/impact`, { headers: authHeaders() });
      setImpacts(prev => ({...prev, [exp.experiment_id]: r.data}));
    } catch {
      setImpacts(prev => ({...prev, [exp.experiment_id]: {status:"error"}}));
    }
  };

  const fetchExperiments = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/experiments`, { headers: authHeaders() });
      const exps = r.data.experiments || [];
      setExperiments(exps);
      exps.forEach(fetchImpact);
    } catch {
      toast.error("Error al cargar experimentos");
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchExperiments(); }, [fetchExperiments]);

  const handleCreate = async (formData) => {
    try {
      const r = await axios.post(`${API}/experiments`, formData, { headers: authHeaders() });
      setExperiments(prev => [r.data, ...prev]);
      setShowModal(false);
      toast.success("Experimento guardado");
      fetchImpact(r.data);
    } catch {
      toast.error("Error al guardar experimento");
    }
  };

  const handleDelete = async (experimentId) => {
    try {
      await axios.delete(`${API}/experiments/${experimentId}`, { headers: authHeaders() });
      setExperiments(prev => prev.filter(e => e.experiment_id !== experimentId));
      setImpacts(prev => { const n = {...prev}; delete n[experimentId]; return n; });
      toast.success("Experimento eliminado");
    } catch {
      toast.error("Error al eliminar");
    }
  };

  return (
    <Card data-testid="experiments-history-block">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-violet-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <FlaskConical className="w-5 h-5 text-white"/>
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">Experiments History</h2>
              <p className="text-xs text-slate-500 mt-0.5">Registra cambios en tu tienda y mide su impacto en el funnel</p>
            </div>
          </div>
          <Button size="sm" onClick={()=>setShowModal(true)} className="gap-1.5" data-testid="add-experiment-btn">
            <Plus className="w-3.5 h-3.5"/> Añadir experimento
          </Button>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1,2].map(i=>(
              <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse"/>
            ))}
          </div>
        ) : experiments.length === 0 ? (
          <div className="text-center py-10 border-2 border-dashed border-slate-200 rounded-xl">
            <FlaskConical className="w-8 h-8 text-slate-300 mx-auto mb-2"/>
            <p className="text-sm font-semibold text-slate-500">Sin experimentos registrados</p>
            <p className="text-xs text-slate-400 mt-1 max-w-xs mx-auto">Registra los cambios que realizas en tu tienda y mide automáticamente si funcionaron.</p>
            <Button size="sm" variant="outline" className="mt-3 gap-1.5" onClick={()=>setShowModal(true)}>
              <Plus className="w-3.5 h-3.5"/> Primer experimento
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {experiments.map(exp=>(
              <ExperimentCard
                key={exp.experiment_id}
                experiment={exp}
                impact={impacts[exp.experiment_id]}
                onDelete={()=>handleDelete(exp.experiment_id)}
              />
            ))}
          </div>
        )}

        {showModal && (
          <AddExperimentModal onClose={()=>setShowModal(false)} onCreate={handleCreate}/>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Date presets ─────────────────────────────────────────────────────────────
const DATE_PRESETS=[
  {label:"Últimos 7 días",  from:subDays(new Date(),6),  to:new Date()},
  {label:"Últimos 14 días", from:subDays(new Date(),13), to:new Date()},
  {label:"Últimos 30 días", from:subDays(new Date(),29), to:new Date()},
  {label:"Últimos 90 días", from:subDays(new Date(),89), to:new Date()},
];

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function FunnelOpportunitiesPage(){
  const [dateRange,  setDateRange]  = useState({from:subDays(new Date(),29),to:new Date()});
  const [pending,    setPending]    = useState({from:subDays(new Date(),29),to:new Date()});
  const [dateOpen,   setDateOpen]   = useState(false);
  const [sector,     setSector]     = useState("recambios_moto");
  const [sectorSaved,setSectorSaved]= useState(false);
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [simOpen,    setSimOpen]    = useState(false);
  const [simDeltas,  setSimDeltas]  = useState({atcCo:0,coBuy:0});
  const simRef = useRef(null);

  // Load org default sector
  useEffect(()=>{
    const token=localStorage.getItem("auth_token");
    axios.get(`${API}/organization/settings`,{headers:token?{Authorization:`Bearer ${token}`}:{}})
      .then(r=>{const s=r.data?.settings?.sector;if (s&&SECTORS[s]) setSector(s);})
      .catch(()=>{});
  },[]);

  const fetchData = useCallback(async()=>{
    if (!dateRange.from||!dateRange.to) return;
    setLoading(true);
    try{
      const token=localStorage.getItem("auth_token");
      const r=await axios.get(`${API}/analytics/funnel-simulator`,{
        params:{start_date:format(dateRange.from,"yyyy-MM-dd"),end_date:format(dateRange.to,"yyyy-MM-dd")},
        headers:token?{Authorization:`Bearer ${token}`}:{},
      });
      setData(r.data);
    }catch{toast.error("Error al cargar datos del funnel");}
    finally{setLoading(false);}
  },[dateRange]);

  useEffect(()=>{fetchData();},[fetchData]);

  const saveSector=async(key)=>{
    setSector(key);
    try{
      const token=localStorage.getItem("auth_token");
      await axios.patch(`${API}/organization/settings`,{sector:key},{headers:token?{Authorization:`Bearer ${token}`}:{}});
      setSectorSaved(true);setTimeout(()=>setSectorSaved(false),2000);
    }catch{toast.error("No se pudo guardar el sector");}
  };

  const applyDate=()=>{if (!pending.from||!pending.to) return;setDateRange(pending);setDateOpen(false);};

  const handleSensCell=({atcCo,coBuy})=>{
    setSimDeltas({atcCo,coBuy});setSimOpen(true);
    setTimeout(()=>simRef.current?.scrollIntoView({behavior:"smooth"}),100);
  };

  const ga4Connected=data?.ga4_connected??false;
  const ga4HasEvents=data?.ga4_has_events??false;
  const ga4HasData  =ga4Connected&&ga4HasEvents;

  const opportunities=data?calcOpportunities(data.funnel,data.financials,sector):[];
  const scoreData=data&&ga4HasData?calcFunnelScore(data.funnel,sector):{score:0,label:"Sin datos",color:"slate",worstMetric:null};
  const totalOpp=calcTotalOpp(opportunities);

  // Last data update
  const lastSyncLabel=data?.last_sync
    ?formatDistanceToNow(new Date(data.last_sync),{addSuffix:true,locale:es})
    :null;

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-5">

        {/* ── HEADER ── */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-black text-slate-800">Funnel Opportunities</h1>
            <p className="text-sm text-slate-500 mt-0.5">Diagnóstico del embudo real · benchmark por sector · oportunidades económicas</p>
            {lastSyncLabel&&(
              <p className="flex items-center gap-1 text-xs text-slate-400 mt-1">
                <Clock className="w-3 h-3"/> Datos actualizados {lastSyncLabel}
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2">
              <select value={sector} onChange={e=>saveSector(e.target.value)}
                className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm font-semibold bg-white focus:outline-none focus:ring-2 focus:ring-violet-300"
                data-testid="sector-select">
                {Object.entries(SECTORS).map(([k,{label}])=><option key={k} value={k}>{label}</option>)}
              </select>
              {sectorSaved&&<span className="text-xs text-emerald-600 font-semibold animate-pulse">Guardado</span>}
            </div>

            <Popover open={dateOpen} onOpenChange={o=>{setDateOpen(o);if (o) setPending(dateRange);}}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2" data-testid="date-picker-btn">
                  <Calendar className="w-4 h-4"/>
                  {dateRange.from&&dateRange.to
                    ?`${format(dateRange.from,"d MMM",{locale:es})} – ${format(dateRange.to,"d MMM yyyy",{locale:es})}`
                    :"Seleccionar fechas"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-3" align="end">
                <div className="flex gap-2 mb-3 flex-wrap">
                  {DATE_PRESETS.map(p=>(
                    <button key={p.label} onClick={()=>setPending({from:p.from,to:p.to})}
                      className="text-xs px-2 py-1 rounded bg-slate-100 hover:bg-violet-100 hover:text-violet-700 font-medium transition-colors">
                      {p.label}
                    </button>
                  ))}
                </div>
                <CalendarComponent mode="range" selected={pending} onSelect={r=>r&&setPending(r)} numberOfMonths={2} locale={es}/>
                <div className="flex justify-end mt-2">
                  <Button size="sm" onClick={applyDate} disabled={!pending.from||!pending.to}>Aplicar</Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* ── LOADING ── */}
        {loading?(
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-500"/>
          </div>
        ):(
          <>
            {/* ── Funnel Score + Total Opportunity ── */}
            {ga4HasData&&(
              <>
                <FunnelScorePanel scoreData={scoreData} totalOpp={totalOpp} ga4HasData={ga4HasData}/>
                {opportunities.length>0&&<TopOpportunityCard opportunities={opportunities}/>}
                <RevenueLeakBlock funnel={data?.funnel} financials={data?.financials} sectorKey={sector} ga4HasData={ga4HasData}/>
              </>
            )}

            {/* ── Without full GA4 data ── */}
            {!ga4HasData&&(
              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="p-5 text-center">
                  <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2"/>
                  <p className="font-bold text-amber-900">Datos de funnel incompletos</p>
                  <p className="text-sm text-amber-700 mt-1 max-w-lg mx-auto">
                    {!ga4Connected
                      ?"Conecta GA4 para ver las tasas del funnel y desbloquear el análisis completo de oportunidades."
                      :"GA4 está conectado pero sin eventos de e-commerce. Verifica addToCarts, checkouts y ecommercePurchases."}
                  </p>
                  <Link to="/integrations"><Button size="sm" className="mt-3">Ir a Integraciones</Button></Link>
                </CardContent>
              </Card>
            )}

            {/* ── Bloque 1 ── */}
            <FunnelBlock funnel={data?.funnel} financials={data?.financials}
              ga4Connected={ga4Connected} ga4HasEvents={ga4HasEvents} sectorKey={sector}/>

            {/* ── Bloque 2 ── */}
            <BenchmarkBlock funnel={data?.funnel} financials={data?.financials}
              sectorKey={sector} ga4HasData={ga4HasData}/>

            {/* ── Blocks 3-6 only with full GA4 data ── */}
            {ga4HasData&&<OpportunitiesBlock opportunities={opportunities}/>}

            {ga4HasData&&(
              <div ref={simRef}>
                <SimulatorBlock base={data} isOpen={simOpen} onToggle={()=>setSimOpen(o=>!o)} externalDeltas={simDeltas}/>
              </div>
            )}

            {ga4HasData&&<SensitivityBlock base={data} onApplyDelta={handleSensCell}/>}
            {ga4HasData&&<ActionsBlock opportunities={opportunities}/>}

            {/* ── Experiments History – always visible ── */}
            <ExperimentsHistory/>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
